import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import api, { extractErrorMessage } from '@app/services/api';
import RichTextEditor from '@modules/database/work-items/frontend/editors/RichTextEditor';
import SearchableSelect from '@platform/ui/SearchableSelect';
import IssuePickerInput from '@modules/database/work-items/frontend/components/IssuePickerInput';
import MultiSelectEditor from '@modules/database/work-items/frontend/editors/MultiSelectEditor';
import DateField from '@modules/database/work-items/frontend/editors/DateField';
import ValidatorRecoveryModal from '@modules/database/work-items/frontend/components/ValidatorRecoveryModal';
import { tipTapToAdf } from '@modules/database/work-items/frontend/utils/adfConverter';
import { DraftLines } from '@modules/database/line-items/frontend/LineItemsField';
import lineItemsApi from '@modules/database/line-items/frontend/api';
import '@modules/database/work-items/frontend/components/CreateIssueModal/CreateIssueModal.css';

// A Line items table on the create screen (a quote on a new order) is
// filled as a DRAFT — kept in the browser, computed by the server — and its
// lines are saved the moment the work item exists. onChange must be stable:
// DraftLines recomputes whenever it changes.
function DraftLinesField({ field, projectKey, drafts, initial = null, valueOf = null }) {
  const onChange = useCallback((d) => { drafts.current[field.id] = d; }, [field.id, drafts]);
  useEffect(() => () => { delete drafts.current[field.id]; }, [field.id, drafts]);
  return (
    <div className="create-issue-field create-issue-field-lines">
      <label className="create-issue-label">{field.name}</label>
      <DraftLines fieldId={field.id} projectKey={projectKey} onChange={onChange} initial={initial}
        paper={{ number: '', valueOf }} />
    </div>
  );
}

// Fields to skip in the create screen — system/non-editable
const SKIP_CREATE_FIELDS = new Set([
  'status', 'created', 'updated', 'creator', 'statuscategorychangedate',
  'aggregatetimespent', 'aggregatetimeoriginalestimate',
  'aggregatetimeestimate', 'aggregateprogress', 'progress',
  'timespent', 'timeestimate',
  // 'security' is NOT skipped: it renders as the scheme's level menu when the
  // project has a work-item security scheme (loadScreen drops it otherwise).
  'attachment', 'worklog', 'comment', 'thumbnail',
  'issuelinks', 'subtasks', 'timetracking', 'watches', 'votes',
  'project', 'issuetype', 'parent', 'resolution',
]);

// Display-name overrides — SYSTEM keys only, whose names are universal. Never a customfield id:
// those ids are tenant data (customfield_10102 was the archive tenant's Story Points, but a
// greenfield tenant's 10102 is whatever was created natively — it rendered a client's "Duration"
// as "Story Points"). The field's own registry name always wins for custom fields.
const FIELD_NAME_OVERRIDES = {
};

// createmeta's `defaultValue` is Jira-shaped; the form inputs hold the raw
// control value. This is the mirror of the submit-side shaping below: an option
// becomes its id, a user becomes the accountId, a number becomes a string
// because the input is text.
function toFormValue(dv) {
  if (dv === null || dv === undefined) return '';
  if (Array.isArray(dv)) return dv.map(v => (v && typeof v === 'object' ? (v.id ?? v.value ?? '') : v));
  if (typeof dv === 'object') {
    if (dv.accountId) return dv.accountId;
    if (dv.id !== undefined) return String(dv.id);
    if (dv.value !== undefined) return String(dv.value);
    return '';
  }
  if (typeof dv === 'number') return String(dv);
  return dv;
}

// mode:
//   'subtask'   — every sub-task type of the project; the new item's parent field = parentIssueKey
//   'linked'    — every standard type; the new item is linked to parentIssueKey (link type from linkInfo)
//   'hierarchy' — the Architectural Hierarchy's allowed children of the open
//                 item (hierarchy.options, from resolveHierarchyCreateOptions),
//                 preselected to hierarchy.initialTypeId. The chosen option's
//                 EDGE decides how the child attaches: a sub-task/child edge
//                 sets the parent field, a linked edge creates the edge's link
//                 type in the edge's direction — the same wiring as the
//                 Structure "+" inline create. onCreated gets the edge back.
// issueTypeId / issueTypeName: the modal opens ON that one work type — no
// type to choose, sub-task or not. A page whose button says "+ New Stock
// Pull" means that type, not "one of the project's base types". When the
// locked type (or any chosen type) is a sub-task and no parentIssueKey was
// given, the form asks for the parent: a picker over the project's work
// items, required, written as the parent field.
// embedded: the same screen drawn IN its caller (a page's one-screen create:
// project → parent → these fields → Create), not as a modal of its own —
// no overlay, no header; the work-type row and the "created under" banner
// are left to the caller when the type is fixed. Fields, validators, draft
// lines, Create and the post-create wiring are exactly the modal's.
// draftLines: { [fieldId]: [{ productKey, productLabel, qty?, vals? }] } — a
// Line items table on the screen opens with these lines (a replenishment
// order made from a filtered stock list); initialSummary fills the summary.
// initialSummary: the summary the screen opens with — what a reader already
// typed before a Create validator asked for more (a grid's draft row).
// initialTypeId: the work type the screen opens on, where the caller knows it.
// presetFields: values written onto the work item as it is created, whatever
// the screen shows — in the API's own shape, last, so nothing on the screen
// overrules them (a support desk's request type raised from Stride: its
// preset answers and its Request Type). presetNote says so on the screen.
function CreateIssueModal({ isOpen, onClose, onCreated, mode, projectKey, projectId, parentIssueKey, linkInfo, hierarchy = null, issueTypeId = null, issueTypeName = null, draftLines = null, initialSummary = '', embedded = false, initialTypeId = null, presetFields = null, presetNote = null }) {
  const lockedType = issueTypeId != null || issueTypeName
    ? { id: issueTypeId != null ? String(issueTypeId) : null, name: issueTypeName ? String(issueTypeName).trim().toLowerCase() : null }
    : null;
  const [parentKey, setParentKey] = useState('');

  // Cached user lookup for the assignee/reporter pickers.
  const [issueTypes, setIssueTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  // A sub-task with nobody to hang it on: the form asks for the parent.
  const needsParent = !parentIssueKey && mode !== 'subtask' && mode !== 'hierarchy' && !!issueTypes.find(t => t.id === selectedTypeId)?.subtask;
  const [screenFields, setScreenFields] = useState(null);
  // Line items fields on the create screen: [{ id, name }] — drawn as drafts.
  const [draftFields, setDraftFields] = useState([]);
  const drafts = useRef({});   // fieldId → { lines, errors } from DraftLines
  const [screenLoading, setScreenLoading] = useState(false);
  const [priorities, setPriorities] = useState(null);
  const [users, setUsers] = useState(null);
  const [components, setComponents] = useState(null);
  const [versions, setVersions] = useState(null);
  const [fieldOptions, setFieldOptions] = useState({});
  const [fieldSchemas, setFieldSchemas] = useState({});
  const [formValues, setFormValues] = useState({});
  // The project's security scheme + levels ({ scheme, levels }); levels
  // empty = no scheme, and the Security Level field is not drawn.
  const [securityLevels, setSecurityLevels] = useState(null);
  // What the create will demand, known BEFORE submit:
  //   requiredFields — { [fieldId]: reason } from the field configuration's
  //     required flag (createmeta) and from the Create transition's
  //     required-field validators (the same gate the create runs, dry-run
  //     on the empty form via /api/issue/create-preflight).
  //   offScreenRequired — [{ id, name, reason }] the validators demand but
  //     the create screen does not show; told up front instead of as a 400.
  const [requiredFields, setRequiredFields] = useState({});
  const [offScreenRequired, setOffScreenRequired] = useState([]);
  const [fieldNames, setFieldNames] = useState({});
  const [descriptionValue, setDescriptionValue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [typesLoading, setTypesLoading] = useState(false);
  const overlayRef = useRef(null);

  // Link type state (managed internally when mode === 'linked')
  const [linkTypes, setLinkTypes] = useState(null);
  const [linkTypeId, setLinkTypeId] = useState('');
  const [linkDirection, setLinkDirection] = useState('outward');

  // Workflow-validator recovery — opens when the create returns 400
  // with errorMessages from a workflow validator. The modal collects
  // the missing fields and retries the create against this payload.
  const [recoveryState, setRecoveryState] = useState(null);
  // Shape: { originalFields, errorMessages }

  // Load issue types on open
  useEffect(() => {
    if (!isOpen) return;
    setTypesLoading(true);
    setError(null);
    setFormValues({});
    setDescriptionValue(null);
    setScreenFields(null);
    setFieldOptions({});
    setParentKey('');

    const loadInitial = async () => {
      try {
        // Hierarchy mode already holds the project's live types (the
        // resolver intersected the tree with them) — no second round-trip.
        const hierarchyTypes = mode === 'hierarchy'
          ? (hierarchy?.options || []).map(o => ({ id: o.issueTypeId, name: o.issueTypeName, iconUrl: o.iconUrl, subtask: !!o.edge?.subtask }))
          : null;
        const [types, prios, userList] = await Promise.all([
          hierarchyTypes ? Promise.resolve(hierarchyTypes) : api.getProjectIssueTypes(projectKey),
          priorities ? Promise.resolve(priorities) : api.getPriorities().catch(() => []),
          users ? Promise.resolve(users) : api.getUsers().catch(() => []),
        ]);

        const filtered = hierarchyTypes ? hierarchyTypes
          : lockedType
            ? types.filter(t => (lockedType.id && String(t.id) === lockedType.id) || (lockedType.name && String(t.name || '').trim().toLowerCase() === lockedType.name))
            : mode === 'subtask'
              ? types.filter(t => t.subtask)
              : types.filter(t => !t.subtask);
        if (lockedType && !filtered.length) setError(`This project has no "${issueTypeName || issueTypeId}" work type.`);
        setIssueTypes(filtered);
        if (!priorities) setPriorities(prios);
        if (!users) setUsers(userList);

        const wantType = hierarchyTypes ? hierarchy?.initialTypeId : initialTypeId;
        const initial = wantType
          ? filtered.find(t => String(t.id) === String(wantType))
          : null;
        if (initial) {
          setSelectedTypeId(initial.id);
        } else if (filtered.length > 0) {
          setSelectedTypeId(filtered[0].id);
        }
      } catch (err) {
        setError('Failed to load issue types');
      } finally {
        setTypesLoading(false);
      }
    };
    loadInitial();
  }, [isOpen, mode, projectKey, hierarchy, initialTypeId]);

  // The hierarchy edge behind the selected type (hierarchy mode only).
  const hierarchyEdge = mode === 'hierarchy'
    ? (hierarchy?.options || []).find(o => String(o.issueTypeId) === String(selectedTypeId))?.edge || null
    : null;
  const hierarchyIsSubtask = !!hierarchyEdge && (hierarchyEdge.relationshipType === 'subtask' || hierarchyEdge.relationshipType === 'child');

  // Fetch link types when mode is 'linked' — auto-select "Link" type with "Child" direction
  useEffect(() => {
    if (!isOpen || mode !== 'linked') return;
    if (linkInfo?.linkTypes) {
      const types = linkInfo.linkTypes;
      setLinkTypes(types);
      const linkType = types.find(t => /^link$/i.test(t.name)) || types[0];
      setLinkTypeId(linkType?.id || '');
      setLinkDirection((linkType?.inward || '').toLowerCase().includes('child') ? 'inward' : 'outward');
    } else {
      api.getIssueLinkTypes().then(data => {
        const types = data.issueLinkTypes || [];
        setLinkTypes(types);
        const linkType = types.find(t => /^link$/i.test(t.name)) || types[0];
        setLinkTypeId(linkType?.id || '');
        setLinkDirection((linkType?.inward || '').toLowerCase().includes('child') ? 'inward' : 'outward');
      }).catch(() => setLinkTypes([]));
    }
  }, [isOpen, mode]);

  // Load screen layout + components/versions when type changes
  useEffect(() => {
    if (!isOpen || !selectedTypeId || !projectId) return;
    setScreenLoading(true);
    setScreenFields(null);
    setDraftFields([]);

    const loadScreen = async () => {
      try {
        const [screen, comps, vers, createMeta, allJiraFields, security] = await Promise.all([
          api.getScreenLayout(projectId, selectedTypeId, 'create').catch(() => null),
          components ? Promise.resolve(components) : api.getProjectComponents(projectKey).catch(() => []),
          versions ? Promise.resolve(versions) : api.getProjectVersions(projectKey).catch(() => []),
          api.getCreateMeta(projectKey, selectedTypeId).catch(() => ({})),
          api.getFields().catch(() => []),
          api.getProjectSecurityLevels(projectKey).catch(() => null),
        ]);
        setSecurityLevels(security);
        const hasSecurityScheme = !!(security && Array.isArray(security.levels) && security.levels.length);

        // Build field schemas: prefer createMeta, fallback to /fields endpoint
        const schemas = { ...(createMeta || {}) };
        if (Array.isArray(allJiraFields)) {
          for (const f of allJiraFields) {
            if (f.id?.startsWith('customfield_') && !schemas[f.id] && f.schema) {
              schemas[f.id] = {
                type: f.schema.type,
                items: f.schema.items,
                system: f.schema.system,
                custom: f.schema.custom,
              };
            }
          }
        }
        setFieldSchemas(schemas);
        const names = {};
        if (Array.isArray(allJiraFields)) for (const f of allJiraFields) if (f.id) names[f.id] = f.name;
        for (const [fid, sc] of Object.entries(schemas)) if (sc?.name) names[fid] = sc.name;
        setFieldNames(names);
        const configRequired = {};
        for (const [fid, sc] of Object.entries(createMeta || {})) {
          if (sc?.required) configRequired[fid] = 'Required by the field configuration';
        }
        configRequired.summary = configRequired.summary || 'Required';
        setRequiredFields(configRequired);
        setOffScreenRequired([]);

        // Seed the form from the field's context DEFAULT VALUE, so the create
        // screen opens showing what Jira would show. createmeta already carries
        // it (metadata-handlers → field-defaults); nothing was reading it, so a
        // field with a default rendered blank.
        //
        // The server also applies defaults for fields left untouched, but only
        // for keys the payload OMITS — so seeding here is what makes the value
        // visible before saving, and keeps what is shown and what is stored the
        // same thing.
        const seeded = {};
        if (initialSummary) seeded.summary = initialSummary;
        for (const [fid, sc] of Object.entries(schemas)) {
          if (!sc || sc.defaultValue === undefined || sc.defaultValue === null) continue;
          seeded[fid] = toFormValue(sc.defaultValue);
        }
        if (Object.keys(seeded).length) {
          // Never clobber something the user has already typed.
          setFormValues(prev => ({ ...seeded, ...prev }));
        }

        if (!components) setComponents(comps);
        if (!versions) setVersions(vers);

        if (screen?.tabs?.length > 0) {
          // Flatten all tab fields, filter out skippable ones. The CREATE
          // screen is the truth for create (Jira parity) — never filtered by
          // the view layout: a field placed on the create screen but not on
          // the view screen (e.g. a "Sub-Tasks to Create" field a workflow
          // validator demands) must still be fillable here.
          const allFields = [];
          const lineFields = [];
          for (const tab of screen.tabs) {
            for (const f of tab.fields) {
              if (SKIP_CREATE_FIELDS.has(f.id)) continue;
              // A Line items table: a draft under the fields, saved after the create.
              // Only where the field's context covers this work type (createmeta
              // is context-filtered): a screen shared by several types shows a
              // quote on the one type it belongs to.
              if (schemas[f.id]?.type === 'lineitems') { if (!createMeta || createMeta[f.id]) lineFields.push({ id: f.id, name: f.name || allJiraFields?.find?.((x) => x.id === f.id)?.name || 'Lines' }); continue; }
              // A Whiteboard is drawn on the work item once it exists.
              if (schemas[f.id]?.type === 'whiteboard') continue;
              // Security Level shows only where a scheme gives it a menu
              // (Jira hides the field on projects without one).
              if (f.id === 'security' && !hasSecurityScheme) continue;
              allFields.push(f);
            }
          }
          setScreenFields(allFields);
          setDraftFields(lineFields);

          // Scheme default level (Jira parity): pre-select it so what is
          // shown is what will be stored. The server applies the same
          // default when the field is absent from the screen altogether.
          const defaultLevelId = hasSecurityScheme ? security.scheme?.defaultLevelId : null;
          if (defaultLevelId && allFields.some(f => f.id === 'security')) {
            setFormValues(prev => (prev.security === undefined ? { ...prev, security: String(defaultLevelId) } : prev));
          }

          // Load options for custom select fields
          const customSelects = allFields.filter(f =>
            f.id.startsWith('customfield_') && !fieldOptions[f.id]
          );
          if (customSelects.length > 0) {
            const optionPromises = customSelects.map(f =>
              api.getFieldOptions(f.id, projectKey, selectedTypeId)
                .then(result => {
                  const opts = Array.isArray(result) ? result : (result?.values || result?.allowedValues || []);
                  return { id: f.id, options: opts };
                })
                .catch(() => ({ id: f.id, options: [] }))
            );
            const results = await Promise.all(optionPromises);
            const newOptions = {};
            for (const r of results) {
              newOptions[r.id] = r.options;
            }
            setFieldOptions(prev => ({ ...prev, ...newOptions }));
          }
        } else {
          // Fallback: minimal fields
          setScreenFields([
            { id: 'summary', name: 'Summary' },
            { id: 'description', name: 'Description' },
            { id: 'priority', name: 'Priority' },
            { id: 'assignee', name: 'Assignee' },
          ]);
        }
      } catch (err) {
        setError('Failed to load create screen fields');
      } finally {
        setScreenLoading(false);
      }
    };
    loadScreen();
  }, [isOpen, selectedTypeId, projectId, projectKey]);

  // A custom user field lists only its context's allowed users (Jira's group /
  // project-role restriction): one scoped fetch per such field on the screen.
  // Assignee / reporter keep the shared list.
  const [scopedUsers, setScopedUsers] = useState({});
  useEffect(() => {
    if (!isOpen || !screenFields || !selectedTypeId) return undefined;
    const ids = screenFields.map(f => f.id).filter(id => id.startsWith('customfield_') && fieldSchemas[id]?.type === 'user');
    if (!ids.length) { setScopedUsers({}); return undefined; }
    let cancelled = false;
    Promise.all(ids.map(id => api.getUsers('', { fieldId: id, projectKey, projectId, issueTypeId: selectedTypeId })
      .then(list => [id, list]).catch(() => [id, null])))
      .then(pairs => { if (!cancelled) setScopedUsers(Object.fromEntries(pairs.filter(([, l]) => Array.isArray(l)))); });
    return () => { cancelled = true; };
  }, [isOpen, screenFields, fieldSchemas, selectedTypeId, projectKey, projectId]);

  // Dry-run the Create transition's validators on the empty form the
  // moment the screen is known, so a required-field validator marks its
  // fields NOW rather than answering with a 400 after the user submits.
  // Fail-open like the gate itself: a preflight error marks nothing extra.
  useEffect(() => {
    if (!isOpen || !screenFields || !selectedTypeId || !projectKey) return;
    let cancelled = false;
    const item = {
      projectKey,
      issuetype: { id: selectedTypeId },
      fields: { project: { key: projectKey }, issuetype: { id: selectedTypeId }, summary: '' },
    };
    const wantsParent = mode === 'subtask' || (mode === 'hierarchy' && hierarchyIsSubtask);
    if (wantsParent && parentIssueKey) item.parentRef = { mode: 'KEY', value: parentIssueKey };
    api.createPreflight([item]).then((res) => {
      if (cancelled) return;
      const onScreen = new Set(screenFields.map(f => f.id));
      const demanded = {};
      for (const b of (res?.blockers || [])) {
        for (const fid of (b.fields || [])) {
          if (!demanded[fid]) demanded[fid] = b.message || 'Required by a workflow validator';
        }
      }
      if (Object.keys(demanded).length === 0) return;
      setRequiredFields(prev => ({ ...demanded, ...prev }));
      setOffScreenRequired(Object.entries(demanded)
        .filter(([fid]) => !onScreen.has(fid))
        .map(([fid, reason]) => ({ id: fid, name: fieldNames[fid] || fid, reason })));
    }).catch(() => {});
    return () => { cancelled = true; };
    // fieldNames arrives with screenFields in the same load; keyed on the screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, screenFields, selectedTypeId, projectKey, mode, hierarchyIsSubtask, parentIssueKey]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  // Close on Escape (embedded: the caller's frame owns Escape)
  useEffect(() => {
    if (!isOpen || embedded) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, embedded]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  const updateField = useCallback((fieldId, value) => {
    setFormValues(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  // Wire up the post-create flow (linked-issue creation + onCreated +
  // onClose). Shared between the happy path and the validator-recovery
  // retry path so both routes end with the same state mutations.
  const finishCreate = useCallback(async (result) => {
    // The draft lines go onto the new work item, in order. A refused line
    // never undoes the create: the item opens and the line is added there.
    for (const [fieldId, d] of Object.entries(drafts.current)) {
      for (const l of (d?.lines || [])) {
        try { await lineItemsApi.addLine(result.key, fieldId, { productKey: l.productKey, productLabel: l.productLabel, vals: l.vals }); }
        catch (lineErr) { console.error(`Work item ${result.key} created but a line of ${fieldId} did not save:`, lineErr); }
      }
    }
    if (mode === 'hierarchy' && hierarchyEdge && !hierarchyIsSubtask) {
      // Linked edge. Identify the type by id when the hierarchy stored one
      // (survives a rename), name as the fallback for older nodes. Edge
      // direction = the phrase side the CHILD (new item) displays; Jira POST
      // semantics put the outward-phrase issue in body.inwardIssue.
      const type = hierarchyEdge.linkTypeId
        ? { id: String(hierarchyEdge.linkTypeId), name: hierarchyEdge.linkTypeName }
        : { name: hierarchyEdge.linkTypeName };
      const linkPayload = hierarchyEdge.linkDirection === 'inward'
        ? { type, inwardIssue: { key: parentIssueKey }, outwardIssue: { key: result.key } }
        : { type, inwardIssue: { key: result.key }, outwardIssue: { key: parentIssueKey } };
      try {
        await api.linkIssue(linkPayload);
      } catch (linkErr) {
        console.error('Work item created but linking to its parent failed:', linkErr);
      }
      onCreated(result, mode, hierarchyEdge);
      onClose();
      return;
    }
    const resolvedLinkType = linkTypes?.find(t => t.id === linkTypeId);
    if (mode === 'linked' && resolvedLinkType) {
      try {
        const linkPayload = {
          type: { name: resolvedLinkType.name },
        };
        if (linkDirection === 'outward') {
          linkPayload.inwardIssue = { key: parentIssueKey };
          linkPayload.outwardIssue = { key: result.key };
        } else {
          linkPayload.inwardIssue = { key: result.key };
          linkPayload.outwardIssue = { key: parentIssueKey };
        }
        await api.linkIssue(linkPayload);
      } catch (linkErr) {
        console.error('Issue created but linking failed:', linkErr);
      }
    }
    onCreated(result, mode, mode === 'linked' ? { linkTypeId, direction: linkDirection, linkTypes } : (mode === 'hierarchy' ? hierarchyEdge : null));
    onClose();
  }, [linkTypes, linkTypeId, linkDirection, mode, parentIssueKey, onCreated, onClose, hierarchyEdge, hierarchyIsSubtask]);

  const handleSubmit = useCallback(async () => {
    const summary = (formValues.summary || '').trim();
    if (needsParent && !parentKey) {
      const typeName = issueTypes.find(t => t.id === selectedTypeId)?.name || 'sub-task';
      setError(`A ${typeName} needs a parent — pick the work item it belongs to.`);
      return;
    }
    if (!summary) {
      setError('Summary is required');
      return;
    }
    setSubmitting(true);
    setError(null);

    // Declared outside the try: the catch hands it to the validator
    // recovery modal, and a const inside the try is not in scope there.
    let fields = null;
    try {
      fields = {
        project: { key: projectKey },
        issuetype: { id: selectedTypeId },
        summary,
      };

      if (mode === 'subtask' && parentIssueKey) {
        fields.parent = { key: parentIssueKey };
      }

      if (mode === 'hierarchy' && hierarchyIsSubtask && parentIssueKey) {
        fields.parent = { key: parentIssueKey };
      }

      if (mode === 'child' && parentIssueKey) {
        fields.customfield_10014 = parentIssueKey;
      }

      if (needsParent && parentKey) {
        fields.parent = { key: parentKey };
      }

      // Description
      if (descriptionValue) {
        fields.description = tipTapToAdf(descriptionValue);
      }

      // Priority
      if (formValues.priority) {
        fields.priority = { id: formValues.priority };
      }

      // Assignee
      if (formValues.assignee) {
        fields.assignee = { accountId: formValues.assignee };
      }

      // Security level — only when the field was on the screen. A chosen
      // level goes as { id }; "No restriction" goes as an explicit null so
      // the server does NOT apply the scheme's default over it.
      if (formValues.security !== undefined && (screenFields || []).some(f => f.id === 'security')) {
        fields.security = formValues.security ? { id: formValues.security } : null;
      }

      // Reporter
      if (formValues.reporter) {
        fields.reporter = { accountId: formValues.reporter };
      }

      // Labels
      if (formValues.labels) {
        fields.labels = formValues.labels.split(',').map(l => l.trim()).filter(Boolean);
      }

      // Components
      if (formValues.components) {
        fields.components = [{ id: formValues.components }];
      }

      // Fix Versions
      if (formValues.fixVersions) {
        fields.fixVersions = [{ id: formValues.fixVersions }];
      }

      // Due date
      if (formValues.duedate) {
        fields.duedate = formValues.duedate;
      }

      // Start Date (stored as customfield_10015 in Jira)
      if (formValues.customfield_10015) {
        fields.customfield_10015 = formValues.customfield_10015;
      }

      // Original Estimate
      if (formValues.timeoriginalestimate) {
        fields.timetracking = { originalEstimate: formValues.timeoriginalestimate };
      }

      // Custom fields — use schema to format values correctly for Jira
      if (screenFields) {
        for (const f of screenFields) {
          if (f.id.startsWith('customfield_') && formValues[f.id] !== undefined && formValues[f.id] !== '' && !(Array.isArray(formValues[f.id]) && formValues[f.id].length === 0)) {
            const schema = fieldSchemas[f.id];
            const schemaType = schema?.type;

            const isMultiSelect = (schemaType === 'array' && (schema?.items === 'option' || schema?.custom?.includes('multiselect')))
              || schema?.custom?.includes('multiselect');

            if (schemaType === 'date' || schemaType === 'datetime') {
              // Date fields: plain string value (YYYY-MM-DD)
              fields[f.id] = formValues[f.id];
            } else if (schemaType === 'number') {
              // Number fields: parse to number
              fields[f.id] = Number(formValues[f.id]);
            } else if (isMultiSelect) {
              // Multi-select: array of { id }
              const val = formValues[f.id];
              fields[f.id] = Array.isArray(val) ? val.map(v => ({ id: v.id || v })) : [{ id: val }];
            } else if (schemaType === 'option' || (fieldOptions[f.id] && fieldOptions[f.id].length > 0)) {
              // Select field: wrap in { id }
              fields[f.id] = { id: formValues[f.id] };
            } else if (schemaType === 'user') {
              // User field: wrap in { accountId }
              fields[f.id] = { accountId: formValues[f.id] };
            } else {
              fields[f.id] = formValues[f.id];
            }
          }
        }
      }

      if (Object.values(drafts.current).some((d) => (d?.errors || []).length)) {
        setError('Fix the lines marked in red first.');
        return;
      }
      // Presets last, so nothing on the screen overrules them.
      if (presetFields) Object.assign(fields, presetFields);
      const result = await api.createIssue({ fields });
      await finishCreate(result);
    } catch (err) {
      // Workflow validator on the create transition? Open the recovery
      // modal so the user can fill in the missing fields and retry.
      const errorMessages = err.response?.data?.details?.errorMessages;
      if (Array.isArray(errorMessages) && errorMessages.length > 0) {
        setRecoveryState({
          originalFields: fields,
          errorMessages,
          missingFields: err.response?.data?.details?.missingFields || [],
        });
      } else {
        setError(extractErrorMessage(err, 'Failed to create issue'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [formValues, descriptionValue, selectedTypeId, projectKey, parentIssueKey, mode, linkTypes, linkTypeId, linkDirection, screenFields, fieldOptions, fieldSchemas, finishCreate, hierarchyIsSubtask, needsParent, parentKey, issueTypes]);

  // Build issue type options for SearchableSelect
  const issueTypeOptions = issueTypes.map(t => ({
    id: t.id,
    name: t.name,
    iconUrl: t.iconUrl ? api.getJiraImageUrl(t.iconUrl) : undefined,
  }));

  // Build priority options
  const priorityOptions = (priorities || []).map(p => ({
    id: p.id,
    name: p.name,
    iconUrl: p.iconUrl,
  }));

  // Build user options (for assignee/reporter)
  const toUserOptions = (list) => (list || [])
    .filter(u => u.accountType === 'atlassian' && u.active !== false)
    .map(u => ({
      id: u.accountId,
      name: u.displayName,
      avatarUrls: u.avatarUrls,
    }));
  const userOptions = toUserOptions(users);

  // Build component options
  const componentOptions = (components || []).map(c => ({
    id: c.id,
    name: c.name,
  }));

  // Build version options
  const versionOptions = (versions || []).map(v => ({
    id: v.id,
    name: v.name,
  }));

  const selectedType = issueTypes.find(t => t.id === selectedTypeId);

  // The red asterisk: drawn when the field configuration or a Create
  // validator demands the field; the reason rides on the tooltip.
  const requiredMark = (fieldId) => {
    const reason = requiredFields[fieldId];
    if (!reason) return null;
    return <span className="create-issue-required" title={reason} aria-label="required">*</span>;
  };

  // A field of the form, as text, by its NAME (a draft document's party and
  // date read the form around them): option ids become their labels.
  const formText = (name) => {
    const want = String(name || '').trim().toLowerCase();
    const fid = Object.keys(fieldNames).find((id) => String(fieldNames[id] || '').trim().toLowerCase() === want)
      || (want === 'due date' ? 'duedate' : null);
    if (!fid) return '';
    const v = formValues[fid];
    if (v == null || v === '') return '';
    const opts = fieldOptions[fid] || [];
    const label = (x) => { const id = x && typeof x === 'object' ? x.id : x; const o = opts.find((y) => String(y.id) === String(id)); return o ? (o.value || o.name || '') : (typeof x === 'object' ? (x.value || x.name || '') : String(x)); };
    return Array.isArray(v) ? v.map(label).filter(Boolean).join(', ') : label(v);
  };

  const renderField = (field) => {
    const fieldId = field.id;
    const fieldName = FIELD_NAME_OVERRIDES[fieldId] || field.name;
    const mark = requiredMark(fieldId);

    // Summary
    if (fieldId === 'summary') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <input
            type="text"
            className="create-issue-input"
            value={formValues.summary || ''}
            onChange={(e) => updateField('summary', e.target.value)}
            disabled={submitting}
            autoFocus
          />
        </div>
      );
    }

    // Description
    if (fieldId === 'description') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <div className="create-issue-description">
            <RichTextEditor
              value={descriptionValue}
              onChange={setDescriptionValue}
              disabled={submitting}
            />
          </div>
        </div>
      );
    }

    // Priority
    if (fieldId === 'priority') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <SearchableSelect
            options={priorityOptions}
            value={formValues.priority || ''}
            onChange={(val) => updateField('priority', val)}
            disabled={submitting}
            placeholder="Search priorities..."
          />
        </div>
      );
    }

    // Assignee
    if (fieldId === 'assignee') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <SearchableSelect
            options={userOptions}
            value={formValues.assignee || ''}
            onChange={(val) => updateField('assignee', val)}
            disabled={submitting}
            placeholder="Search users..."
            noneLabel="Unassigned"
          />
        </div>
      );
    }

    // Security level — the project's scheme levels; "No restriction" clears it.
    if (fieldId === 'security') {
      const levelOptions = (securityLevels?.levels || []).map(l => ({ id: String(l.id), name: l.name }));
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName || 'Security Level'}{mark}</label>
          <SearchableSelect
            options={levelOptions}
            value={formValues.security || ''}
            onChange={(val) => updateField('security', val)}
            disabled={submitting}
            placeholder="Search security levels..."
            noneLabel="No restriction"
          />
        </div>
      );
    }

    // Reporter
    if (fieldId === 'reporter') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <SearchableSelect
            options={userOptions}
            value={formValues.reporter || ''}
            onChange={(val) => updateField('reporter', val)}
            disabled={submitting}
            placeholder="Search users..."
            noneLabel="Default"
          />
        </div>
      );
    }

    // Labels
    if (fieldId === 'labels') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <input
            type="text"
            className="create-issue-input"
            placeholder="Comma-separated labels"
            value={formValues.labels || ''}
            onChange={(e) => updateField('labels', e.target.value)}
            disabled={submitting}
          />
        </div>
      );
    }

    // Components
    if (fieldId === 'components') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <SearchableSelect
            options={componentOptions}
            value={formValues.components || ''}
            onChange={(val) => updateField('components', val)}
            disabled={submitting}
            placeholder="Search components..."
          />
        </div>
      );
    }

    // Fix Versions
    if (fieldId === 'fixVersions') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <SearchableSelect
            options={versionOptions}
            value={formValues.fixVersions || ''}
            onChange={(val) => updateField('fixVersions', val)}
            disabled={submitting}
            placeholder="Search versions..."
          />
        </div>
      );
    }

    // Due date
    if (fieldId === 'duedate') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <DateField
            className="create-issue-input"
            value={formValues.duedate || ''}
            onChange={(v) => updateField('duedate', v)}
            disabled={submitting}
          />
        </div>
      );
    }

    // Start Date (may appear as 'startDate' or 'customfield_10015')
    if (fieldId === 'startDate' || fieldId === 'customfield_10015') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName || 'Start Date'}{mark}</label>
          <DateField
            className="create-issue-input"
            value={formValues.customfield_10015 || formValues.startDate || ''}
            onChange={(v) => updateField('customfield_10015', v)}
            disabled={submitting}
          />
        </div>
      );
    }

    // Custom fields — use schema to determine input type
    if (fieldId.startsWith('customfield_')) {
      const schema = fieldSchemas[fieldId];
      const schemaType = schema?.type;
      console.log(`[Create] renderField ${fieldId} (${fieldName}): schema=`, schema);

      // Date or datetime → date picker
      if (schemaType === 'date' || schemaType === 'datetime' || schema?.custom?.includes('datepicker') || schema?.custom?.includes('datetime')) {
        return (
          <div key={fieldId} className="create-issue-field">
            <label className="create-issue-label">{fieldName}{mark}</label>
            <DateField
              className="create-issue-input"
              value={formValues[fieldId] || ''}
              onChange={(v) => updateField(fieldId, v)}
              disabled={submitting}
            />
          </div>
        );
      }

      // Number → number input
      if (schemaType === 'number') {
        return (
          <div key={fieldId} className="create-issue-field">
            <label className="create-issue-label">{fieldName}{mark}</label>
            <input
              type="number"
              className="create-issue-input"
              value={formValues[fieldId] || ''}
              onChange={(e) => updateField(fieldId, e.target.value)}
              disabled={submitting}
            />
          </div>
        );
      }

      // Multi-select fields
      const isMultiSelect = (schemaType === 'array' && (schema?.items === 'option' || schema?.custom?.includes('multiselect')))
        || schema?.custom?.includes('multiselect');
      if (isMultiSelect && fieldOptions[fieldId]?.length > 0) {
        const customOpts = (fieldOptions[fieldId] || []).map(opt => ({
          id: String(opt.id || opt.value),
          value: opt.value || opt.name || opt.label,
          name: opt.value || opt.name || opt.label,
        }));
        return (
          <div key={fieldId} className="create-issue-field">
            <label className="create-issue-label">{fieldName}{mark}</label>
            <MultiSelectEditor
              value={formValues[fieldId] || []}
              onChange={(val) => updateField(fieldId, val)}
              options={customOpts}
              disabled={submitting}
              collapsible
            />
          </div>
        );
      }

      // Single option select or has fetched options
      if (schemaType === 'option' || fieldOptions[fieldId]?.length > 0) {
        const customOpts = (fieldOptions[fieldId] || []).map(opt => ({
          id: opt.id || opt.value,
          name: opt.value || opt.name || opt.label,
        }));
        if (customOpts.length > 0) {
          return (
            <div key={fieldId} className="create-issue-field">
              <label className="create-issue-label">{fieldName}{mark}</label>
              <SearchableSelect
                options={customOpts}
                value={formValues[fieldId] || ''}
                onChange={(val) => updateField(fieldId, val)}
                disabled={submitting}
                placeholder={`Search ${fieldName.toLowerCase()}...`}
                noneLabel="None"
              />
            </div>
          );
        }
      }

      // User type → user select
      if (schemaType === 'user') {
        return (
          <div key={fieldId} className="create-issue-field">
            <label className="create-issue-label">{fieldName}{mark}</label>
            <SearchableSelect
              options={scopedUsers[fieldId] ? toUserOptions(scopedUsers[fieldId]) : userOptions}
              value={formValues[fieldId] || ''}
              onChange={(val) => updateField(fieldId, val)}
              disabled={submitting}
              placeholder={`Search users...`}
              noneLabel="None"
            />
          </div>
        );
      }

      // Fallback → text input
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <input
            type="text"
            className="create-issue-input"
            value={formValues[fieldId] || ''}
            onChange={(e) => updateField(fieldId, e.target.value)}
            disabled={submitting}
          />
        </div>
      );
    }

    // Original Estimate
    if (fieldId === 'timeoriginalestimate') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <input
            type="text"
            className="create-issue-input"
            placeholder="e.g. 1w 2d 4h 30m"
            value={formValues[fieldId] || ''}
            onChange={(e) => updateField(fieldId, e.target.value)}
            disabled={submitting}
          />
        </div>
      );
    }

    // Environment
    if (fieldId === 'environment') {
      return (
        <div key={fieldId} className="create-issue-field">
          <label className="create-issue-label">{fieldName}{mark}</label>
          <input
            type="text"
            className="create-issue-input"
            value={formValues[fieldId] || ''}
            onChange={(e) => updateField(fieldId, e.target.value)}
            disabled={submitting}
          />
        </div>
      );
    }

    // Fallback: skip unknown system fields
    return null;
  };

  if (!isOpen) return null;

  // Portalled to <body>: the item panel lives under a transformed ancestor
  // in Structure and the search results (the sticky-panel scroll trick), and
  // a transform turns position:fixed into position:relative-to-it — the
  // modal was landing somewhere in the scrolled table instead of the
  // viewport. TransitionScreenModal does the same.
  // Embedded with one fixed type: the caller already shows the type and the parent.
  const callerShowsPlacement = embedded && (!!lockedType || (mode === 'hierarchy' && issueTypeOptions.length <= 1));
  const content = (
      <div className={`${embedded ? 'create-issue-embedded' : 'modal-content'} create-issue-modal${draftFields.length ? ' create-issue-has-lines' : ''}`}>
        {/* Header */}
        {!embedded && (
        <div className="modal-header">
          <h2>{mode === 'subtask' ? 'Create Subtask' : (mode === 'hierarchy' && selectedType?.name) ? `Create ${selectedType.name}` : 'Create Work Item'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {/* Hierarchy banner — the parent this child is created under */}
          {mode === 'hierarchy' && parentIssueKey && !callerShowsPlacement && (
            <div className="create-issue-link-banner">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4"/><path d="M8 12h8"/><path d="M8 12v2"/><path d="M16 12v2"/>
                <circle cx="12" cy="8" r="2"/><circle cx="8" cy="16" r="2"/><circle cx="16" cy="16" r="2"/>
              </svg>
              Will be created under <strong>{parentIssueKey}</strong>{hierarchyIsSubtask ? ' as a sub-task' : ' as a child'}
            </div>
          )}

          {/* Link info banner */}
          {mode === 'linked' && (
            <div className="create-issue-link-banner">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Will be linked to <strong>{parentIssueKey}</strong> as a child
            </div>
          )}

          {/* Issue type selector */}
          <div className="create-issue-type-row" style={callerShowsPlacement ? { display: 'none' } : undefined}>
            <label className="create-issue-label">Work Type</label>
            <SearchableSelect
              options={issueTypeOptions}
              value={selectedTypeId}
              onChange={(val) => {
                if (val) {
                  setSelectedTypeId(val);
                  setFormValues(prev => ({ summary: prev.summary || '' }));
                  setDescriptionValue(null);
                }
              }}
              disabled={submitting || typesLoading || !!lockedType}
              placeholder="Search work types..."
              noneLabel={typesLoading ? 'Loading...' : 'Select type'}
              iconUrl={selectedType?.iconUrl ? api.getJiraImageUrl(selectedType.iconUrl) : undefined}
            />
          </div>

          {needsParent && (
            <div className="create-issue-type-row">
              <label className="create-issue-label">Parent <span className="create-issue-required" title="A sub-task belongs to a work item in the same project" aria-label="required">*</span></label>
              <IssuePickerInput value={parentKey} onChange={(k) => setParentKey(k || '')} project={projectKey} candidateRole="parent"
                placeholder={`The work item this ${selectedType?.name || 'sub-task'} belongs to…`} disabled={submitting} />
            </div>
          )}
          {presetNote && <div className="create-issue-offscreen-required" role="note">{presetNote}</div>}

          {/* Fields the Create validators demand that this screen does not
              show — said now, not as a 400 after Create. */}
          {!screenLoading && offScreenRequired.length > 0 && (
            <div className="create-issue-offscreen-required" role="note">
              <strong>Also required by the workflow, but not on this screen:</strong>
              <ul>
                {offScreenRequired.map(f => <li key={f.id} title={f.reason}>{f.name}</li>)}
              </ul>
            </div>
          )}

          {/* Fields */}
          {screenLoading ? (
            <div className="create-issue-loading">
              <span className="screen-spinner"></span>
              Loading fields...
            </div>
          ) : screenFields ? (
            <div className="create-issue-fields">
              {screenFields.map(f => renderField(f))}
              {draftFields.map(f => <DraftLinesField key={f.id} field={f} projectKey={projectKey} drafts={drafts} initial={draftLines && draftLines[f.id] ? draftLines[f.id] : null} valueOf={formText} />)}
            </div>
          ) : null}

          {/* Error */}
          {error && (
            <div className="create-issue-error">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="save-button"
            onClick={handleSubmit}
            disabled={submitting || screenLoading || !screenFields}
          >
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
  );
  return (
    <>
    {embedded ? content : createPortal(
      <div className="modal-overlay" ref={overlayRef} onClick={handleOverlayClick}>{content}</div>,
      document.body,
    )}

    <ValidatorRecoveryModal
      isOpen={!!recoveryState}
      originalFields={recoveryState?.originalFields}
      errorMessages={recoveryState?.errorMessages}
      missingFields={recoveryState?.missingFields}
      projectKey={projectKey}
      issuetypeId={selectedTypeId}
      onSubmitted={async (createdIssue) => {
        setRecoveryState(null);
        try {
          await finishCreate(createdIssue);
        } catch (err) {
          console.error('Post-create wiring failed after validator recovery:', err);
        }
      }}
      onCancel={() => setRecoveryState(null)}
    />
    </>
  );
}

export default CreateIssueModal;
